import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import type { Row } from "./api";
import { useAdmin } from "./context";
import { clock, countdown, hasRef, isTest, L, queueRows, refLine, waitKind, type QueueKind } from "./format";
import { NoteDialog } from "./NoteDialog";
import { Empty, IcLink, Loading, LoadError, PageHead, TestChip, TONE, useServerNow } from "./parts";

/** The queue's one query: the tab bar's count (index.tsx) reads the same cache. */
export const queueQuery = (key: string, api: ReturnType<typeof useAdmin>["api"]) => ({
  queryKey: [key, "queue"],
  queryFn: () => api.get<Row>("queue"),
});

// The portal's rem is 20 px and the rail takes its share of 1440 px. Intercom clips a long ticket number; Mark handled sits last.
const QUEUE_COLS = ["10.5rem", "7rem", "10.5rem", null, "7.5rem", "7.5rem"];

type Handled = { by: string; at: string; note: boolean };
/** The queue with one item marked handled here (all three lists, so every reader sees it). */
export function markHandled(d: Row | undefined, kind: QueueKind, id: unknown, mark: Handled): Row | undefined {
  if (!d) return d;
  const k = kind === "Alert" ? "alert" : "handoff";
  const hit = (x: Row, kk: string) => kk === k && x.id === id ? { ...x, handled_here: mark } : x;
  return {
    ...d,
    items: Array.isArray(d.items) ? (d.items as Row[]).map((x) => hit(x, x.kind)) : d.items,
    handoffs: ((d.handoffs || []) as Row[]).map((x) => hit(x, "handoff")),
    alerts: ((d.alerts || []) as Row[]).map((x) => hit(x, "alert")),
  };
}
/** Open items: what the tab counts. A row handled on this screen no longer waits; a test account never counts. */
export const openCount = (d: Row | undefined) =>
  (Array.isArray(d?.items) ? (d!.items as Row[]) : [...((d?.handoffs || []) as Row[]), ...((d?.alerts || []) as Row[])])
    .filter((x) => !x.handled_here && !isTest(x)).length;

/** The queue's order with test accounts last: they have no real deadline (Damien 31 Oct). */
export const inboxRows = (d: Row | undefined): [QueueKind, Row][] => {
  const all = queueRows(d);
  return [...all.filter(([, x]) => !isTest(x)), ...all.filter(([, x]) => isTest(x))];
};

/** PA1 words, verbatim (DEC-1164). */
const MARK_FAILED = "Couldn’t mark it handled. Nothing changed. Try again.";

/** Inbox (v3.1 PA1; renamed from "Open hand-offs & alerts", Damien 30 Oct): time left to reply, smallest first; each customer was promised 3 working days. */
export function QueueTab() {
  const { key, api, me, openCustomer } = useAdmin();
  const client = useQueryClient();
  const q = useQuery(queueQuery(key, api));
  const now = useServerNow(q.data?.server_now, q.dataUpdatedAt);

  const rows = inboxRows(q.data);

  const row = (kind: QueueKind, x: Row) => {
    const rid = `${kind}-${x.id}`;
    const h = x.handled_here as Handled | undefined;
    const test = isTest(x);
    const c = h || test ? null : countdown(x.reply_due_at, now);
    return (
      <TableRow
        key={rid}
        // The whole row opens the customer (Damien 30 Oct): highlight on hover, any click, Enter or Space.
        className={x.user_id ? "cursor-pointer hover:bg-ks-purple-4 focus-visible:bg-ks-purple-4 focus-visible:outline-none" : undefined}
        tabIndex={x.user_id ? 0 : undefined}
        role={x.user_id ? "link" : undefined}
        aria-label={x.user_id ? `Open customer ${x.name || x.email || ""}`.trim() : undefined}
        onClick={x.user_id ? () => {
          // A drag to copy text is not a click on the row.
          if (window.getSelection()?.toString()) return;
          openCustomer(x.user_id);
        } : undefined}
        onKeyDown={x.user_id ? (e) => {
          if (e.target !== e.currentTarget || (e.key !== "Enter" && e.key !== " ")) return;
          e.preventDefault();
          openCustomer(x.user_id);
        } : undefined}
      >
        <TableCell className="align-top">
          {h ? (
            <span className="font-semibold text-ks-pass">Handled</span>
          ) : test ? (
            <span className="text-ks-grey-1">Test: no deadline</span>
          ) : c ? (
            <div className={TONE[c.tone]}>
              <div className="font-semibold tabular-nums whitespace-nowrap">{c.text}</div>
              <div className="text-xs">{c.by}</div>
            </div>
          ) : (
            "—"
          )}
        </TableCell>
        <TableCell className="align-top whitespace-normal">{waitKind(kind, x)}</TableCell>
        <TableCell className="align-top break-words whitespace-normal">
          <div className="font-semibold">{x.name || x.email || "—"}{test ? <> <TestChip /></> : null}</div>
          {x.name && x.email ? <div className="text-xs text-ks-grey-1">{x.email}</div> : null}
          {x.goal ? <div className="text-xs">{x.goal}</div> : null}
        </TableCell>
        <TableCell className="align-top whitespace-normal">
          {h ? (
            <span>Handled by {h.by} at {clock(h.at)}{h.note ? " with a note" : ""}.</span>
          ) : (
            <span className="line-clamp-2" title={why(x)}>{why(x)}</span>
          )}
        </TableCell>
        {/* The Intercom link and Mark handled (its dialog renders in a portal but bubbles here) keep their own clicks. */}
        {/* Intercom ticket numbers run to 15+ digits: the cell clips with an ellipsis (full number on hover) so it
            never runs under Mark handled (Damien 31 Oct). */}
        <TableCell className="min-w-0 overflow-hidden align-top" onClick={(e) => e.stopPropagation()}>
          {x.ticket ? (
            <span className="block truncate" title={`#${String(x.ticket)}`}>
              <IcLink id={x.ticket}>#{String(x.ticket)}</IcLink>
            </span>
          ) : (
            <span className="block whitespace-normal text-ks-grey-1">{x.ticket_error ? L(x.ticket_error) : "Creating ticket…"}</span>
          )}
        </TableCell>
        <TableCell className="align-top" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <div className="flex flex-col items-stretch gap-1.5">
            {me?.writes && !h ? (
              <NoteDialog
                label="Mark handled"
                title="Mark handled"
                description="Optional note: what was done?"
                failure={MARK_FAILED}
                onSave={async (note) => {
                  const r = await api.post<Row>("handled", { kind: kind === "Alert" ? "alert" : "handoff", id: x.id, note });
                  // The row stays in place, saying who handled it, until the next load drops it; the shared cache
                  // marks it so the tab count and the opening tab stop treating it as waiting.
                  const mark: Handled = { by: r.handled_by || me.email, at: r.handled_at || new Date(now).toISOString(), note: !!note.trim() };
                  client.setQueryData<Row>([key, "queue"], (d) => markHandled(d, kind, x.id, mark));
                  void client.invalidateQueries({ queryKey: [key, "customer"] });
                }}
              />
            ) : null}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div>
      <PageHead title="Inbox" sub="Hand-offs and alerts waiting on a reply, longest wait first. Each customer was promised a reply within 3 working days." />
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <LoadError what="load" error={q.error} />
      ) : (
        <Card>
          <CardContent>
            {rows.length ? (
              <Table className="table-fixed">
                {/* 1440 px with no sideways scroll (next-release list): fixed widths, Why takes the rest and clamps at two lines. */}
                <colgroup>
                  {QUEUE_COLS.map((w, i) => <col key={i} style={w ? { width: w } : undefined} />)}
                </colgroup>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time left to reply</TableHead>
                    <TableHead>Kind</TableHead>
                    <TableHead>Customer and goal</TableHead>
                    <TableHead>Why</TableHead>
                    <TableHead>Intercom</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>{rows.map(([kind, x]) => row(kind, x))}</TableBody>
              </Table>
            ) : (
              <QueueEmpty />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function why(x: Row): string {
  if (hasRef(x.referral)) return refLine(x.referral);
  return x.why || x.note || (x.reason ? L(x.reason) : "") || "—";
}

/** PA1E: nothing waiting. */
function QueueEmpty() {
  const { openCustomer } = useAdmin();
  return (
    <Empty>
      <p className="text-base font-semibold text-ks-ink">Nothing waiting. Nice.</p>
      <p className="mt-1">Every customer who asked has a reply.</p>
      <Button className="mt-3" variant="outline" data-nonmutating onClick={() => openCustomer(null)}>Go to Customers</Button>
    </Empty>
  );
}
